import React from 'react';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';

const testimonials = [
  {
    nameEn: 'Sarah M.',
    nameAr: 'سارة م.',
    roleEn: 'Hifz Program · UK',
    roleAr: 'برنامج الحفظ · المملكة المتحدة',
    textEn: "Hudan completely transformed my Quran learning journey. My teacher is incredibly patient and my Hifz progress has been remarkable.",
    textAr: 'هُدًى غيّر رحلتي في تعلم القرآن تمامًا. معلمتي صبورة للغاية وتقدمي في الحفظ كان مذهلًا.',
    rating: 5,
  },
  {
    nameEn: 'Ahmed K.',
    nameAr: 'أحمد ك.',
    roleEn: 'Tajweed Course · UAE',
    roleAr: 'دورة التجويد · الإمارات',
    textEn: "The AI Tajweed feedback is a game-changer. I can practice anytime and get instant corrections on my recitation.",
    textAr: 'تقييم التجويد بالذكاء الاصطناعي ثورة حقيقية. أستطيع التدرب في أي وقت والحصول على تصحيحات فورية.',
    rating: 5,
  },
  {
    nameEn: 'Fatima A.',
    nameAr: 'فاطمة أ.',
    roleEn: 'Parent of two · Saudi Arabia',
    roleAr: 'ولي أمر لطفلين · السعودية',
    textEn: "My children love their Quran classes. The family dashboard makes it easy to track all their progress in one place.",
    textAr: 'أطفالي يحبون دروس القرآن. لوحة الأسرة تسهّل تتبع تقدمهم جميعًا في مكان واحد.',
    rating: 5,
  },
  {
    nameEn: 'Yusuf R.',
    nameAr: 'يوسف ر.',
    roleEn: 'Quranic Arabic · Canada',
    roleAr: 'العربية القرآنية · كندا',
    textEn: "I'd tried apps and YouTube for years before Hudan. Having a teacher correct my grammar live, verse by verse, is what finally made it click.",
    textAr: 'جربت التطبيقات ودروس يوتيوب لسنوات قبل هُدًى. وجود معلم يصحح قواعدي مباشرة، آية بآية، هو ما جعل الأمور تتضح أخيرًا.',
    rating: 5,
  },
  {
    nameEn: 'Khadija O.',
    nameAr: 'خديجة أ.',
    roleEn: 'New Muslim · United States',
    roleAr: 'حديثة عهد بالإسلام · الولايات المتحدة',
    textEn: "As a revert with no Arabic background, I was nervous to start. My teacher made me feel at ease from the first session. Reading Al-Fatiha properly is a moment I will never forget.",
    textAr: 'كحديثة عهد بالإسلام بدون خلفية عربية، كنت قلقة من البداية. معلمتي جعلتني أشعر بالراحة منذ الجلسة الأولى. قراءة الفاتحة بشكل صحيح لحظة لن أنساها.',
    rating: 5,
  },
  {
    nameEn: 'Mohammed H.',
    nameAr: 'محمد ح.',
    roleEn: 'Tafseer Course · Egypt',
    roleAr: 'دورة التفسير · مصر',
    textEn: "The Tafseer course goes deeper than I expected. We compare classical commentaries verse by verse — exactly the kind of structured study I was missing.",
    textAr: 'دورة التفسير تذهب أعمق بكثير مما توقعت. نقارن التفاسير الكلاسيكية آية بآية — هذا بالضبط نوع الدراسة المنهجية الذي كان ينقصني.',
    rating: 5,
  },
];

const Testimonials = () => {
  const { isRTL } = useLanguage();

  return (
    <section className="section bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <AnimateOnScroll className="text-center mb-14">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2">
            {isRTL ? 'آراء الطلاب' : 'Testimonials'}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            {isRTL ? 'ماذا يقول طلابنا' : 'What Our Students Say'}
          </h2>
        </AnimateOnScroll>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <StaggerItem key={i}>
              <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col relative">
                <div className="absolute top-4 end-4">
                  <Quote className="h-8 w-8 text-primary/10" />
                </div>
                <div
                  className="flex gap-0.5 mb-4"
                  aria-label={isRTL ? `${t.rating} من 5 نجوم` : `${t.rating} out of 5 stars`}
                >
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 text-amber-500 fill-amber-500" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-foreground/80 text-sm leading-relaxed flex-1 mb-5">
                  "{isRTL ? t.textAr : t.textEn}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm">
                      {(isRTL ? t.nameAr : t.nameEn).charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{isRTL ? t.nameAr : t.nameEn}</p>
                    <p className="text-xs text-muted-foreground">{isRTL ? t.roleAr : t.roleEn}</p>
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default Testimonials;
